import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Gift, Users, Eye, Edit, Trash2, Plus, Loader2, UserCircle, Settings } from 'lucide-react';
import { toast } from 'sonner';

const SorteosList = () => {
  const navigate = useNavigate();
  const [sorteos, setSorteos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSorteos();
  }, []);

  const fetchSorteos = async () => {
    try {
      const response = await axios.get(`${API}/sorteos/my-sorteos`);
      setSorteos(response.data);
    } catch (error) {
      console.error('Error fetching sorteos:', error);
      toast.error('Error al cargar los sorteos');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (sorteoId, title) => {
    if (!window.confirm(`¿Estás seguro de eliminar el sorteo "${title}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/sorteos/${sorteoId}`);
      toast.success('Sorteo eliminado');
      fetchSorteos();
    } catch (error) {
      console.error('Error deleting sorteo:', error);
      toast.error('Error al eliminar el sorteo');
    }
  };

  const getSorteoTypeLabel = (type) => {
    switch (type) {
      case 'grid_100':
        return '00-99 (100)';
      case 'standard':
        return '1000 Números (000-999)';
      case 'grid_10000':
        return '0000-9999 (10000)';
      case 'lines':
        return '200 Líneas x 5';
      case 'lines_125':
        return '125 Líneas x 8';
      case 'lines_250':
        return '250 filas Manu';
      case 'lines_250_osvaldo':
        return '250 Líneas Osvaldo';
      case 'lines_50':
        return '50 Líneas x 2';
      case 'lines_500':
        return '500 Líneas x 2';
      case 'lines_20':
        return '20 Líneas x 5';
      case 'custom':
        return 'Personalizado';
      case 'clientes':
        return 'Sorteo para Clientes';
      default:
        return type;
    }
  };

  const getStats = (sorteo) => ({
    available: sorteo.stats?.available || 0,
    registered: sorteo.stats?.reserved || 0,
  });

  if (loading) {
    return (
      <div className="text-center py-12">
        <Loader2 className="animate-spin mx-auto mb-4" size={40} />
        <p>Cargando sorteos...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <div>
          <h2 className="text-3xl font-bold">Mis Sorteos</h2>
          <p className="text-gray-600">Sorteos gratuitos - 1 numero por persona</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            onClick={() => navigate('/admin/sorteos/create-clientes')}
            className="bg-purple-600 hover:bg-purple-700 w-full sm:w-auto"
          >
            <UserCircle className="mr-2" size={20} />
            Sorteo para Clientes
          </Button>
          <Button
            onClick={() => navigate('/admin/sorteos/create')}
            className="bg-green-600 hover:bg-green-700 w-full sm:w-auto"
          >
            <Plus className="mr-2" size={20} />
            Crear Sorteo
          </Button>
        </div>
      </div>

      {sorteos.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Gift size={64} className="mx-auto mb-4 text-gray-400" />
            <h3 className="text-xl font-semibold mb-2">No tenés sorteos</h3>
            <p className="text-gray-600 mb-4">
              Creá tu primer sorteo gratuito para que la gente participe
            </p>
            <Button
              onClick={() => navigate('/admin/sorteos/create')}
              className="bg-green-600 hover:bg-green-700"
            >
              <Plus className="mr-2" size={20} />
              Crear mi primer Sorteo
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sorteos.map((sorteo) => {
            const stats = getStats(sorteo);
            return (
              <Card key={sorteo.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  {sorteo.logo && (
                    <div className="mb-2">
                      <img
                        src={sorteo.logo}
                        alt={sorteo.title}
                        className="w-full h-24 object-contain"
                      />
                    </div>
                  )}
                  <div className="flex justify-between items-start">
                    <CardTitle className="text-xl">{sorteo.title}</CardTitle>
                    <Badge variant={sorteo.is_active ? "default" : "secondary"}>
                      {sorteo.is_active ? 'Activo' : 'Inactivo'}
                    </Badge>
                  </div>
                  <CardDescription>
                    {getSorteoTypeLabel(sorteo.sorteo_type)}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Total números:</span>
                      <span className="font-semibold">{sorteo.total_numbers}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 flex items-center">
                        <Users size={14} className="mr-1" />
                        Participantes:
                      </span>
                      <span className="font-semibold text-green-600">{stats.registered}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Disponibles:</span>
                      <span className="font-semibold text-blue-600">{stats.available}</span>
                    </div>
                    <div className="pt-2">
                      <p className="text-sm font-semibold text-gray-700">Premios:</p>
                      <ul className="text-sm text-gray-600">
                        {sorteo.prizes.slice(0, 2).map((prize, idx) => (
                          <li key={idx}>• {prize}</li>
                        ))}
                        {sorteo.prizes.length > 2 && (
                          <li className="text-green-600">+ {sorteo.prizes.length - 2} más</li>
                        )}
                      </ul>
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/admin/sorteos/manage/${sorteo.id}`)}
                    className="flex-1"
                    data-testid={`manage-sorteo-${sorteo.id}`}
                  >
                    <Settings size={16} className="mr-1" />
                    Gestionar
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/admin/sorteos/edit/${sorteo.id}`)}
                  >
                    <Edit size={16} />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(sorteo.id, sorteo.title)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 size={16} />
                  </Button>
                </CardFooter>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SorteosList;
